import { Box, ListItemButton, Typography } from '@mui/material';
import { debounce } from '@cc-shopping/utils/function/debounce';
import { forwardRef, useCallback, useEffect, useRef } from 'react';
import { Checkbox } from './Checkbox';

type Props = {
  option: { label: string; count: number };
  isSelected: boolean;
  onSelect: () => void;
  tabIndex: number;
};

export const OptionItem = forwardRef<HTMLDivElement, Props>(
  ({ option, isSelected, onSelect, tabIndex }, ref) => {
    const stableOnSelectRef = useRef(onSelect);

    useEffect(() => {
      stableOnSelectRef.current = onSelect;
    }, [onSelect]);

    const debouncedOnSelect = useCallback(
      debounce(
        () => {
          stableOnSelectRef.current();
        },
        100,
        { leading: true, trailing: false },
      ),
      [],
    );

    useEffect(() => {
      return () => {
        debouncedOnSelect.cancel();
      };
    }, [debouncedOnSelect]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          debouncedOnSelect();
        }
      },
      [debouncedOnSelect],
    );

    const handleKeyUp = useCallback((event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    }, []);

    const isDisabled = option.count === 0 && !isSelected;

    return (
      <ListItemButton
        ref={ref}
        role="option"
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        tabIndex={tabIndex}
        onKeyDown={isDisabled ? undefined : handleKeyDown}
        onKeyUp={isDisabled ? undefined : handleKeyUp}
        onClick={isDisabled ? undefined : debouncedOnSelect}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isDisabled ? 'default' : 'pointer',
          gap: 2,
          py: 2,
          px: 3,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'blues.light',
          },
          '&:hover': {
            backgroundColor: 'neutral.lightest',
          },
        }}
      >
        <Typography variant="textSmall" sx={{ color: 'neutral.dark', fontSize: '14px' }}>
          {option.label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography
            variant="textSmall"
            sx={{ color: isDisabled ? 'neutral.lighter' : 'neutral.dark', fontSize: '12px' }}
          >
            {option.count}
          </Typography>
          <Checkbox checked={isSelected} disabled={isDisabled} />
        </Box>
      </ListItemButton>
    );
  },
);
